import { LEARNING_PHASES } from '../../Learning/constants/learningPhases';
import {
  CLEAR_LEARNING_QUIZ_RESULT,
  INIT_LEARNING_SESSION,
  RESET_LEARNING_GUIDANCE,
  RESET_LEARNING_QUESTIONNAIRE,
  RESET_LEARNING_SESSION,
  SET_LEARNING_ATTEMPTS_USED,
  SET_LEARNING_INVITE_LOCKED,
  SET_LEARNING_BRAND_SUCCESS,
  CLEAR_LEARNING_BRAND,
  SET_LEARNING_DOCUMENT_SCROLL,
  SET_LEARNING_GUIDANCE_INDEX,
  SET_LEARNING_PACKAGE_ERROR,
  SET_LEARNING_PACKAGE_LOADING,
  SET_LEARNING_PACKAGE_SUCCESS,
  HYDRATE_LEARNING_INVITE_PROGRESS,
  RESET_LEARNING_INVITE_PROGRESS,
  SET_LEARNING_RECIPIENT_EMAIL,
  SET_LEARNING_PHASE,
  SET_LEARNING_QUESTION_INDEX,
  SET_LEARNING_QUESTIONNAIRE_ERROR,
  SET_LEARNING_QUESTIONNAIRE_LOADING,
  SET_LEARNING_QUESTIONNAIRE_SUBMITTED,
  SET_LEARNING_QUESTIONNAIRE_SURVEY,
  SET_LEARNING_QUIZ_RESULT,
} from './constants';

import { learningDefaults } from '../../Learning/constants/learningDefaults';

const defaultBrandState = {
  logoSrc: null,
  packageId: null,
};

const defaultGuidanceState = {
  index: 0,
  documentReadPercent: 0,
  documentComplete: false,
  documentNeedsScroll: false,
};

const defaultQuestionnaireState = {
  survey: null,
  loading: false,
  error: null,
  questionIndex: 0,
  submitted: false,
};

export const INIT_STATE = {
  packageId: null,
  userId: null,
  surveyId: null,
  inviteId: null,
  packageData: null,
  packageLoading: false,
  packageError: null,
  phase: LEARNING_PHASES.INTRO,
  attemptsUsed: 0,
  isInviteLocked: false,
  recipientEmail: '',
  quizResult: null,
  guidance: { ...defaultGuidanceState },
  questionnaire: { ...defaultQuestionnaireState },
  brand: { ...defaultBrandState },
};

function createInitialState() {
  return {
    ...INIT_STATE,
    guidance: { ...defaultGuidanceState },
    questionnaire: { ...defaultQuestionnaireState },
    brand: { ...defaultBrandState },
  };
}

function resetInviteProgressState(state) {
  return {
    ...state,
    phase: LEARNING_PHASES.INTRO,
    attemptsUsed: 0,
    isInviteLocked: false,
    quizResult: null,
    guidance: { ...defaultGuidanceState },
    questionnaire: { ...defaultQuestionnaireState },
  };
}

function isSameSession(state, payload) {
  return state.packageId === payload?.packageId
    && state.userId === payload?.userId
    && state.surveyId === payload?.surveyId
    && state.inviteId === payload?.inviteId;
}

export const learning = (state = INIT_STATE, action) => {
  switch (action.type) {
    case INIT_LEARNING_SESSION: {
      if (isSameSession(state, action.payload)) {
        return resetInviteProgressState({
          ...state,
          packageId: action.payload?.packageId ?? null,
          userId: action.payload?.userId ?? null,
          surveyId: action.payload?.surveyId ?? null,
          inviteId: action.payload?.inviteId ?? null,
        });
      }

      return {
        ...createInitialState(),
        packageId: action.payload?.packageId ?? null,
        userId: action.payload?.userId ?? null,
        surveyId: action.payload?.surveyId ?? null,
        inviteId: action.payload?.inviteId ?? null,
      };
    }

    case RESET_LEARNING_SESSION:
      return createInitialState();

    case SET_LEARNING_PACKAGE_LOADING:
      return {
        ...state,
        packageLoading: action.payload,
        ...(action.payload ? {
          packageData: null,
          packageError: null,
          brand: { ...defaultBrandState },
        } : {}),
      };

    case SET_LEARNING_PACKAGE_SUCCESS:
      return {
        ...state,
        packageData: action.payload,
        packageLoading: false,
        packageError: null,
      };

    case HYDRATE_LEARNING_INVITE_PROGRESS: {
      const progress = action.payload;
      if (!progress || !state.inviteId) {
        return state;
      }

      return {
        ...state,
        attemptsUsed: progress.attemptsUsed ?? state.attemptsUsed,
        isInviteLocked: progress.isLocked ?? state.isInviteLocked,
        phase: progress.phase ?? state.phase,
        quizResult: progress.quizResult ?? null,
      };
    }

    case RESET_LEARNING_INVITE_PROGRESS:
      return resetInviteProgressState(state);

    case SET_LEARNING_RECIPIENT_EMAIL:
      return {
        ...state,
        recipientEmail: typeof action.payload === 'string' ? action.payload : '',
      };

    case SET_LEARNING_PACKAGE_ERROR:
      return {
        ...state,
        packageData: null,
        packageLoading: false,
        packageError: action.payload,
      };

    case SET_LEARNING_PHASE:
      return {
        ...state,
        phase: action.payload,
      };

    case SET_LEARNING_ATTEMPTS_USED:
      return {
        ...state,
        attemptsUsed: Math.max(0, Number(action.payload) || 0),
      };

    case SET_LEARNING_INVITE_LOCKED:
      return {
        ...state,
        isInviteLocked: Boolean(action.payload),
      };

    case SET_LEARNING_QUIZ_RESULT:
      return {
        ...state,
        quizResult: action.payload,
      };

    case CLEAR_LEARNING_QUIZ_RESULT:
      return {
        ...state,
        quizResult: null,
      };

    case SET_LEARNING_GUIDANCE_INDEX:
      return {
        ...state,
        guidance: {
          ...state.guidance,
          index: action.payload,
          documentReadPercent: 0,
          documentComplete: false,
          documentNeedsScroll: false,
        },
      };

    case SET_LEARNING_DOCUMENT_SCROLL:
      return {
        ...state,
        guidance: {
          ...state.guidance,
          ...action.payload,
        },
      };

    case RESET_LEARNING_GUIDANCE:
      return {
        ...state,
        guidance: { ...defaultGuidanceState },
      };

    case SET_LEARNING_QUESTIONNAIRE_LOADING:
      return {
        ...state,
        questionnaire: {
          ...state.questionnaire,
          loading: action.payload,
          ...(action.payload ? { error: null } : {}),
        },
      };

    case SET_LEARNING_QUESTIONNAIRE_SURVEY:
      return {
        ...state,
        questionnaire: {
          ...state.questionnaire,
          survey: action.payload,
          loading: false,
          error: null,
          questionIndex: 0,
          submitted: false,
        },
      };

    case SET_LEARNING_QUESTIONNAIRE_ERROR:
      return {
        ...state,
        questionnaire: {
          ...state.questionnaire,
          survey: null,
          loading: false,
          error: action.payload,
        },
      };

    case SET_LEARNING_QUESTION_INDEX:
      return {
        ...state,
        questionnaire: {
          ...state.questionnaire,
          questionIndex: action.payload,
        },
      };

    case SET_LEARNING_QUESTIONNAIRE_SUBMITTED:
      return {
        ...state,
        questionnaire: {
          ...state.questionnaire,
          submitted: action.payload,
        },
      };

    case RESET_LEARNING_QUESTIONNAIRE:
      return {
        ...state,
        questionnaire: {
          ...defaultQuestionnaireState,
        },
      };

    case SET_LEARNING_BRAND_SUCCESS:
      return {
        ...state,
        brand: {
          logoSrc: action.payload?.logoSrc ?? null,
          packageId: action.payload?.packageId ?? null,
        },
      };

    case CLEAR_LEARNING_BRAND:
      return {
        ...state,
        brand: { ...defaultBrandState },
      };

    default:
      return state;
  }
};
